import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { ExportButtons, fmtQ, fyStart, todayIst } from "./exports";
import { StockReportDrill } from "./StockReportDrill";
import { ItemPicker } from "./ItemPicker";

const inp = "w-full h-9 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm font-mono focus:outline-none focus:border-[var(--accent)]";
const lbl = "text-[10px] font-mono uppercase tracking-widest text-zinc-500";

function Cell({ v, onClick, tid, bold }) {
  const zero = Math.abs(v || 0) < 1e-9;
  const cls = `px-3 py-2 text-right font-mono tabular-nums text-xs ${bold ? "font-semibold text-white" : ""} ${v < 0 ? "text-amber-300" : ""}`;
  if (!onClick || zero) return <td data-testid={tid} className={`${cls} ${zero ? "text-zinc-600" : ""}`}>{fmtQ(v)}</td>;
  return <td className={cls}><button type="button" data-testid={tid} onClick={onClick} className="text-[var(--accent)] hover:underline">{fmtQ(v)}</button></td>;
}

function Filters({ f, setF, godowns, domain, onRun, busy }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
      <div><div className={lbl}>Godown *</div>
        <select data-testid="sr-godown" className={inp} value={f.warehouse_id} onChange={e => setF({ ...f, warehouse_id: e.target.value })}>
          <option value="">Select one Godown</option>
          {godowns.map(g => <option key={g.id} value={g.id}>{`${g.code} · ${g.name}`}</option>)}
        </select></div>
      <div><div className={lbl}>From Date</div><input type="date" data-testid="sr-from" className={inp} value={f.from_date} onChange={e => setF({ ...f, from_date: e.target.value })} /></div>
      <div><div className={lbl}>To Date</div><input type="date" data-testid="sr-to" className={inp} value={f.to_date} onChange={e => setF({ ...f, to_date: e.target.value })} /></div>
      <div><div className={lbl}>Item (optional)</div>
        <ItemPicker domain={domain} warehouseId={f.warehouse_id} value={f.item_id} className={inp}
          onChange={(v) => setF(x => ({ ...x, item_id: v }))} /></div>
      <button type="button" data-testid="sr-run" onClick={onRun} disabled={busy || !f.warehouse_id}
        className="h-9 px-4 rounded-sm bg-[var(--accent)] text-white text-xs font-mono uppercase tracking-widest disabled:bg-zinc-700 disabled:cursor-not-allowed">
        {busy ? "Loading…" : "Run Report"}
      </button>
    </div>
  );
}

function ReportTable({ r, onDrill }) {
  const cols = [{ key: "opening", label: "Opening" }, ...r.columns, { key: "closing", label: "Closing" }];
  return (
    <div className="tactical-card overflow-x-auto" data-testid="sr-table">
      <table className="w-full">
        <thead><tr className="border-b border-[var(--border)] text-[10px] font-mono uppercase tracking-widest text-zinc-500">
          <th className="text-left px-3 py-2">Item Code</th><th className="text-left px-3 py-2">UOM</th>
          {cols.map(c => <th key={c.key} className="text-right px-3 py-2 whitespace-nowrap">{c.label}{c.side ? (c.side > 0 ? " (+)" : " (−)") : ""}</th>)}
        </tr></thead>
        <tbody>
          {r.totals_by_uom.map(t => (
            <tr key={t.uom} className="bg-amber-500/10 border-b-2 border-amber-400/50 font-semibold" data-testid={`sr-total-${t.uom}`}>
              <td className="px-3 py-2 text-xs font-mono text-amber-200">TOTAL ({t.uom})</td><td className="px-3 py-2 text-xs font-mono">{t.uom}</td>
              {cols.map(c => <Cell key={c.key} v={t[c.key]} bold tid={`sr-total-${c.key}-${t.uom}`} />)}
            </tr>
          ))}
          {r.rows.map(x => (
            <tr key={`${x.item_key}|${x.uom}`} className="border-b border-[var(--border)]" data-testid={`sr-row-${x.item_code}`}>
              <td className="px-3 py-2 text-xs font-mono text-[var(--accent)]">{x.item_code}</td>
              <td className="px-3 py-2 text-xs font-mono">{x.uom}</td>
              {cols.map(c => <Cell key={c.key} v={x[c.key]} bold={c.key === "closing"} tid={`sr-cell-${c.key}-${x.item_code}`}
                onClick={() => onDrill({ column: c.key, item: x.item_key, uom: x.uom })} />)}
            </tr>
          ))}
          {r.rows.length === 0 && <tr><td colSpan={cols.length + 2} className="px-3 py-6 text-center text-xs text-zinc-500">No stock movements for this selection.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

// ST-01 · Common Stock Report (RM / SFG / FG / Wastage) · single Godown, read-only.
export function StockReportTab({ domain, godowns }) {
  const [f, setF] = useState({ warehouse_id: "", from_date: fyStart(), to_date: todayIst(), item_id: "" });
  const [r, setR] = useState(null);
  const [busy, setBusy] = useState(false);
  const [drill, setDrill] = useState(null);
  useEffect(() => { setR(null); setF(x => ({ ...x, warehouse_id: "", item_id: "" })); }, [domain]);
  const params = { category: domain, warehouse_id: f.warehouse_id, from_date: f.from_date, to_date: f.to_date, item_id: f.item_id || undefined };
  const [ran, setRan] = useState(params);
  const run = async () => {
    setBusy(true);
    try { const { data } = await api.get("/inventory/v2/stock-report", { params }); setR(data); setRan(params); }
    catch (e) { toast.error(e?.response?.data?.detail?.message || "Report failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-4" data-testid="stock-report-tab">
      <div className="tactical-card p-4 space-y-3">
        <Filters f={f} setF={setF} godowns={godowns} domain={domain} onRun={run} busy={busy} />
        <div className="flex items-center justify-between flex-wrap gap-2">
          <p className="text-[11px] text-zinc-500">Closing = Opening + In − Out · posting date (IST) · reversals shown signed on their own date · Consumption includes production wastage.</p>
          <ExportButtons params={ran} disabled={!r} prefix="sr" />
        </div>
      </div>
      {r && <div className="text-xs font-mono text-zinc-400" data-testid="sr-header">{r.godown.code} · {r.godown.name} · {r.from_date} → {r.to_date} · {r.rows.length} item(s)</div>}
      {r && <ReportTable r={r} onDrill={setDrill} />}
      <StockReportDrill target={drill} params={ran} onClose={() => setDrill(null)} />
    </div>
  );
}
